import { Redirect, useLocalSearchParams } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, ScrollView, View } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { SafeAreaView } from "react-native-safe-area-context";

import { Card, CardContent } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { PickupRequestForm } from "@/features/pickupRequest";
import {
    ActionChildSelector,
    resolveSelectedChildIds,
    useChildren,
} from "@/features/selectedChild";

export default function PickupRequestScreen() {
    const { childId: childIdParam } = useLocalSearchParams<{
        childId?: string;
    }>();
    const { children, isLoading } = useChildren();
    const [manualSelectedChildIds, setManualSelectedChildIds] = useState<
        number[] | null
    >(null);
    const { t } = useTranslation();

    const selectedChildIds = useMemo(
        () =>
            resolveSelectedChildIds(
                manualSelectedChildIds,
                childIdParam,
                children.map((child) => child.id),
            ),
        [manualSelectedChildIds, childIdParam, children],
    );

    const selectedChildren = useMemo(
        () => children.filter((child) => selectedChildIds.includes(child.id)),
        [children, selectedChildIds],
    );

    if (isLoading) {
        return (
            <View className="flex-1 items-center justify-center bg-background">
                <ActivityIndicator />
            </View>
        );
    }

    if (children.length === 0) {
        return <Redirect href="/" />;
    }

    const FormScroll =
        Platform.OS === "web" ? ScrollView : KeyboardAwareScrollView;

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            <FormScroll
                className="flex-1"
                contentContainerClassName="px-4 web:w-full web:mx-auto web:max-w-lg"
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="interactive"
                bottomOffset={24}
            >
                <Card>
                    <CardContent className="gap-4">
                        <ActionChildSelector
                            multiple
                            childList={children}
                            selectedChildIds={selectedChildIds}
                            onChangeSelectedChildIds={setManualSelectedChildIds}
                        />

                        {selectedChildren.length > 0 ? (
                            <PickupRequestForm
                                selectedChildren={selectedChildren}
                            />
                        ) : (
                            <Text variant="muted">
                                {t("ui.selectStudent")}
                            </Text>
                        )}
                    </CardContent>
                </Card>
            </FormScroll>
        </SafeAreaView>
    );
}
